import { useParams, Link, Navigate } from "react-router-dom";
import { hasExpiredFiles } from "@/config/retention";
import { useGenerationStatus } from "@/hooks/use-generation";
import {
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Sparkles,
  Star,
  Download,
  Loader2,
  FastForward,
  Droplets,
  Crown,
  Lock,
  Zap,
  Film,
  Gauge,
} from "lucide-react";
import { useUserStore } from "@/stores/user-store";
import {
  calculateProgress,
  calculateTimeRemaining,
  formatTime,
  inferClipSeconds,
} from "@/lib/generation-utils";
import { useState, useEffect, useCallback } from "react";

const TIPS = [
  "Detailed prompts produce better results",
  "Try describing camera movements for video",
  "Reference specific art styles for unique looks",
  "Mention lighting conditions for mood",
  "Include color palette descriptions",
];

export function GeneratePage() {
  const { id } = useParams<{ id: string }>();
  const { data } = useGenerationStatus(id ?? null);
  const generation = data?.generation;
  const { hasPurchased, credits } = useUserStore();
  const [fallbackStart] = useState(() => Date.now());
  const [, setTick] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [generation?.status]);

  useEffect(() => {
    if (generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 4000);
    return () => clearInterval(interval);
  }, [generation?.status]);

  const clipSeconds = inferClipSeconds(
    generation?.model_id,
    generation?.credits_used,
    generation?.duration_ms,
  );
  const startTime = generation?.created_at
    ? new Date(generation.created_at).getTime()
    : fallbackStart;
  const progress = calculateProgress(startTime, generation?.model_id, clipSeconds);
  const timeRemaining = calculateTimeRemaining(startTime, generation?.model_id, clipSeconds);

  const MODEL_ROUTES: Record<string, string> = {
    qwen_image: "/create/text-to-image",
    qwen_image_edit: "/create/image-edit",
    cs_t2v_480p_5s: "/create/text-to-video",
    cs_480p_i2v_5s: "/create/image-to-video",
    cs_480p_i2v_8s: "/create/image-to-video",
    cs_720p_i2v_5s: "/create/image-to-video",
    cs_720p_i2v_8s: "/create/image-to-video",
    wan25_720p_i2v_5s: "/create/image-to-video",
    integrated_h3_i2v: "/create/image-to-video",
  };
  const createRoute = MODEL_ROUTES[generation?.model_id || ""] || "/create/text-to-image";

  // An old bookmarked result whose files the clean-up deleted: the library shows it as Expired.
  if (generation && hasExpiredFiles(generation)) return <Navigate to="/assets" replace />;

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs text-[#8a8570] hover:text-[#2d3127] transition-colors mb-8 font-medium"
      >
        <ArrowLeft className="size-3.5" />
        Home
      </Link>

      {/* Processing */}
      {(!generation ||
        generation.status === "pending" ||
        generation.status === "processing") && (
        <div className="flex flex-col items-center justify-center py-12 animate-fade-in-up">
          {/* Animated orb */}
          <div className="relative mb-10">
            <div className="absolute inset-0 rounded-full bg-orange-500/10 blur-[40px] animate-pulse-glow" style={{ width: 120, height: 120, margin: "-10px" }} />
            <div className="relative size-24 rounded-full bg-orange-50/80 border border-orange-200/40 flex items-center justify-center animate-pulse-ring">
              <div className="size-14 rounded-full bg-orange-100/80 border border-orange-200/30 flex items-center justify-center">
                <Sparkles className="size-6 text-[#e8822e] animate-float" />
              </div>
            </div>
          </div>

          <h2 className="text-xl font-bold text-[#2d3127] mb-1 tracking-tight">
            Creating your masterpiece
          </h2>
          <p className="text-sm text-[#8a8570] mb-2">
            {timeRemaining !== null
              ? `~${formatTime(timeRemaining)} remaining`
              : "This usually takes about a minute..."}
          </p>

          {/* Rotating tips */}
          <p className="text-[11px] text-[#e8822e]/60 mb-8 h-4 transition-opacity">
            <Star className="size-2.5 inline mr-1 relative -top-px" />
            {TIPS[tipIndex]}
          </p>

          {/* Progress */}
          <div className="w-full max-w-xs">
            <div className="h-1 rounded-full bg-white/40 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#e8822e] transition-all duration-1000 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <p className="text-[10px] text-[#8a8570] tabular-nums">{Math.round(progress)}%</p>
              <p className="text-[10px] text-[#8a8570]">
                {generation?.status === "pending" ? "Queued" : "Processing"}
              </p>
            </div>
          </div>

          {!hasPurchased && (
            <Link
              to="/billing?src=generation_waiting"
              className="mt-10 w-full max-w-sm p-4 rounded-2xl bg-white/50 border border-[rgba(45,49,39,0.08)] hover:border-orange-300/60 hover:bg-white/70 transition-all group block"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="size-8 rounded-xl bg-orange-50 border border-orange-200/50 flex items-center justify-center">
                  <Crown className="size-4 text-[#e8822e]" />
                </div>
                <p className="text-[13px] font-bold text-[#2d3127]">Upgrade Your Creations</p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-1.5">
                  <Lock className="size-3 text-[#e8822e]/70" />
                  <span className="text-[11px] text-[#8a8570]">No watermarks</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="size-3 text-[#e8822e]/70" />
                  <span className="text-[11px] text-[#8a8570]">Better quality models</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Film className="size-3 text-[#e8822e]/70" />
                  <span className="text-[11px] text-[#8a8570]">Longer generations</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Gauge className="size-3 text-[#e8822e]/70" />
                  <span className="text-[11px] text-[#8a8570]">Faster generation times</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-orange-50 border border-orange-200/50 group-hover:bg-orange-100/70 transition-colors">
                <Star className="size-3.5 text-[#e8822e]" />
                <span className="text-[12px] font-bold text-[#e8822e]">Buy Stars</span>
              </div>
            </Link>
          )}
        </div>
      )}

      {/* Completed */}
      {generation?.status === "completed" && generation.url && (() => {
        const isImage = generation.model_id === "qwen_image" || generation.model_id === "qwen_image_edit";
        const displayUrl = hasPurchased ? generation.url : (generation.watermarked_url || generation.url);
        // Only send a rerun back to the create form if the balance actually covers
        // it; otherwise the pack page is the honest destination.
        const canAffordRerun = credits > 0 && credits >= (generation.credits_used ?? 0);
        return (
        <div className="animate-fade-in-up">
          <div className="relative rounded-2xl overflow-hidden border border-white/40 shadow-lg mb-6 bg-black/5 flex items-center justify-center">
            {isImage ? (
              <img src={displayUrl} alt="Generation result" className="w-full max-h-[70vh] object-contain" />
            ) : (
              <video
                src={displayUrl}
                controls
                autoPlay
                loop
                playsInline
                className="w-full max-h-[70vh] object-contain"
              />
            )}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/20">
              <CheckCircle2 className="size-3 text-emerald-400" />
              <span className="text-[10px] font-semibold text-emerald-300">Complete</span>
            </div>
          </div>

          {/* Prominent Extend CTA — post-gen conversion driver */}
          {!isImage && (
            <Link
              to={hasPurchased ? `/create/extend-video?video_id=${generation.id}` : "/billing?src=post_gen_extend"}
              className="mb-4 block w-full p-4 rounded-2xl bg-gradient-to-r from-[#fff4e8] via-[#ffe8d4] to-[#fff4e8] border-2 border-[#e8822e]/40 hover:border-[#e8822e]/70 hover:from-[#ffe8d4] hover:via-[#ffdcbb] hover:to-[#ffe8d4] transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-2xl bg-[#e8822e]/15 border border-[#e8822e]/30 flex items-center justify-center shrink-0">
                  <FastForward className="size-5 text-[#e8822e]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-extrabold text-[#2d3127] tracking-tight">
                    🎬 Add 5–15 seconds more now
                  </p>
                  <p className="text-[12px] text-[#8a8570] mt-0.5 leading-snug">
                    {hasPurchased
                      ? "Continue this scene where it left off — new motion, dialog, or a fresh beat."
                      : "Get stars to extend this video with more action, dialog, or a new scene."}
                  </p>
                </div>
                <ArrowRight className="size-4 text-[#e8822e] shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          )}

          {/* Actions */}
          {!hasPurchased ? (
            <>
              <p className="text-sm text-[#2d3127] text-center mb-4 leading-relaxed">
                Like this quality? Unlock <span className="font-semibold">Enhanced</span> and more generations with any star pack.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Link
                  to="/billing?src=post_gen_watermark"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[13px] font-bold shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-px transition-all"
                >
                  <Droplets className="size-4" /> Remove Watermark
                </Link>
                <Link
                  to={canAffordRerun ? createRoute : "/billing?src=post_gen_regen"}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/70 border border-[rgba(45,49,39,0.1)] text-[13px] font-bold text-[#2d3127] hover:bg-white hover:border-orange-300/60 hover:-translate-y-px transition-all"
                >
                  <RotateCcw className="size-4 text-[#e8822e]" /> Generate Again
                </Link>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                {generation.download_url && (
                  <DownloadButton url={generation.download_url} isImage={isImage} label="Download Preview" />
                )}
                <span className="text-[10px] text-[#b5b0a0]">{generation.credits_used} ★ used</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <Link to={createRoute} className="btn-primary flex items-center gap-2 text-[13px] py-2.5 px-5">
                <Sparkles className="size-3.5" />
                Create Another
              </Link>
              {generation.download_url && <DownloadButton url={generation.download_url} isImage={isImage} />}
              <span className="text-[10px] text-[#b5b0a0]">{generation.credits_used} ★ used</span>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-[13px] text-[#8a8570]">
              Unhappy with a generation? <a href="mailto:support@couchstack.com" className="text-[#e8822e] font-semibold hover:underline">Send us an email</a> and we'll help you out :)
            </p>
          </div>

          {generation.credits_used === 0 && generation.model_id === "integrated_h3_i2v" && (
            <Link
              to="/create/image-to-video"
              className="mt-4 block w-full p-4 rounded-2xl bg-gradient-to-r from-[#fff4e8] via-[#ffe8d4] to-[#fff4e8] border-2 border-[#e8822e]/30 hover:border-[#e8822e]/60 transition-all"
            >
              <p className="text-[15px] font-extrabold text-[#2d3127] tracking-tight mb-1">
                Like what you see?
              </p>
              <p className="text-[13px] text-[#8a8570] leading-relaxed">
                Create more videos like this with <span className="font-bold text-[#e8822e]">Enhanced</span> on the Image to Video page. Same upgraded pipeline, same quality.
              </p>
              <div className="flex items-center gap-2 mt-3 text-[13px] font-bold text-[#e8822e]">
                <Sparkles className="size-4" />
                Go to Image to Video
                <ArrowRight className="size-3.5" />
              </div>
            </Link>
          )}
        </div>
        );
      })()}

      {/* Failed */}
      {generation?.status === "failed" && (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
          <div className="size-14 rounded-full bg-red-50/80 border border-red-200/40 flex items-center justify-center mb-4">
            <XCircle className="size-6 text-red-400" />
          </div>
          <h2 className="text-base font-bold text-[#2d3127] mb-1">
            Generation Failed
          </h2>
          <p className="text-xs text-[#8a8570] mb-1 text-center max-w-sm">
            {generation.error_message || "Something went wrong"}
          </p>
          <p className="text-[11px] text-emerald-600/60 mb-5">
            ★ Stars have been refunded
          </p>
          <Link to={createRoute} className="btn-primary inline-flex items-center gap-2 text-[13px]">
            <RotateCcw className="size-3.5" />
            Try Again
          </Link>
          <p className="text-[12px] text-[#8a8570] mt-5">
            Keep having issues? <a href="mailto:support@couchstack.com" className="text-[#e8822e] font-semibold hover:underline">Email us</a> and we'll make it right.
          </p>
        </div>
      )}
    </div>
  );
}

function DownloadButton({ url, isImage, label = "Download" }: { url: string; isImage: boolean; label?: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const ext = isImage ? "png" : "mp4";
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `couchstack-generation.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }, [url, isImage]);

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="btn-glass flex items-center gap-2 text-[13px] py-2.5 px-5"
    >
      {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
      {label}
    </button>
  );
}
